import React, { ReactNode } from "react";
import { QRCodeSVG } from "qrcode.react";
import CopyableIdentifier from "../controls/CopyableIdentifier";

type HistoryRecordSummaryProps = {
  title: string;
  status?: string | null;
  barcode?: string | number | null;
  actions?: ReactNode;
  children: ReactNode;
};

export function HistoryDetail({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="mt-1 break-words text-sm font-medium text-slate-800 [overflow-wrap:anywhere]">
        {children == null || children === "" ? "—" : children}
      </dd>
    </div>
  );
}

export default function HistoryRecordSummary({
  title,
  status,
  barcode,
  actions,
  children,
}: HistoryRecordSummaryProps) {
  // Match the scanner-friendly payload used by the reference application's QR.
  const value = barcode == null ? "" : String(barcode).split("\u001d").join("").trim();

  return (
    <section aria-label="Record summary" className="rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 sm:px-5">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <h2 className="min-w-0 break-words text-lg font-semibold text-slate-800 [overflow-wrap:anywhere]">{title}</h2>
          {status ? (
            <span className="max-w-full rounded-md bg-slate-100 px-2.5 py-1 text-sm font-medium text-slate-700 [overflow-wrap:anywhere]">
              {status}
            </span>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
      <div className={`grid gap-5 p-4 sm:p-5 ${value ? "lg:grid-cols-[minmax(0,1fr)_208px]" : ""}`}>
        <dl className="grid min-w-0 content-start grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          {children}
        </dl>
        {value ? (
          <div className="min-w-0 border-t border-slate-200 pt-4 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
            <h3 className="text-sm font-medium text-slate-800">Barcode</h3>
            <div className="mt-2 inline-flex rounded-lg border border-slate-200 bg-white p-2">
              <QRCodeSVG
                value={value}
                size={160}
                level="M"
                marginSize={4}
                bgColor="#FFFFFF"
                fgColor="#000000"
                role="img"
                aria-label={`QR code for barcode ${value}`}
              />
            </div>
            <div className="mt-2 text-sm text-slate-800">
              <CopyableIdentifier value={value} description="full barcode" />
            </div>
            <p className="mt-1 text-xs text-slate-500">Scan to use this barcode</p>
          </div>
        ) : null}
      </div>
    </section>
  );
}
